import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { track } from "@/lib/track";
import { useLanguage } from "@/context/LanguageContext";

const darkInput =
  "w-full bg-slate-950 border border-slate-800 px-3 py-2.5 text-sm rounded-sm text-white placeholder-slate-600 focus:outline-none focus:border-gold-500";

export default function ContactForm() {
  const { lang, t } = useLanguage();
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [quotaData, setQuotaData] = useState(null);
  const [errors, setErrors] = useState({});
  const [started, setStarted] = useState(false);
  const [form, setForm] = useState({ company: "", contact_person: "", email: "", quota: "", message: "", website: "" });

  useEffect(() => {
    const applyQuota = (q) => {
      setQuotaData(q);
      const parts = [
        `${q.placements} ${t.calc.devicesUnit}`,
        q.location,
        q.university,
        q.field,
        q.device,
        `${q.weeks} ${t.calc.weeksUnit}`,
      ].filter(Boolean);
      setForm((f) => ({ ...f, quota: f.quota || parts.join(" · ") }));
    };
    const raw = sessionStorage.getItem("lidlink-quota");
    if (raw) {
      try {
        applyQuota(JSON.parse(raw));
      } catch {
        /* ignore malformed quota */
      }
    }
    const handler = (e) => applyQuota(e.detail);
    window.addEventListener("lidlink:quota", handler);
    return () => window.removeEventListener("lidlink:quota", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (key, value) => {
    if (!started) {
      setStarted(true);
      track("form_start");
    }
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
  const valid = form.company.trim() && form.contact_person.trim() && emailOk;

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.company.trim()) errs.company = t.contact.fieldRequired;
    if (!form.contact_person.trim()) errs.contact_person = t.contact.fieldRequired;
    if (!form.email.trim()) errs.email = t.contact.fieldRequired;
    else if (!emailOk) errs.email = t.contact.invalidEmail;
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setSending(true);
    try {
      await api.post("/campaign-requests", {
        ...form,
        location: quotaData?.location || "Kiel",
        university: quotaData?.university || "",
        field_of_study: quotaData?.field || "",
        device: quotaData?.device || "Both",
        placements: quotaData?.placements || 50,
        duration_weeks: quotaData?.weeks || 4,
        language: lang,
      });
      sessionStorage.removeItem("lidlink-quota");
      track("form_submit");
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, t.contact.errorGeneric));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-slate-950 text-white" data-testid="contact-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-12">
          <span className="text-xs font-mono-spec text-gold-300 font-semibold tracking-wider uppercase">{t.contact.eyebrow}</span>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mt-2">{t.contact.title}</h2>
          <p className="text-slate-400 mt-2 text-sm max-w-xl mx-auto">{t.contact.sub}</p>
        </div>

        <div className="bg-slate-900 border border-slate-800 p-6 sm:p-8 rounded-sm">
          {done ? (
            <div className="text-center py-8" data-testid="contact-success">
              <CheckCircle2 className="mx-auto text-emerald-400 mb-4" size={40} />
              <h3 className="text-xl font-bold">{t.contact.successTitle}</h3>
              <p className="text-sm text-slate-400 mt-2">{t.contact.successSub}</p>
            </div>
          ) : (
            <form className="space-y-5" onSubmit={submit} data-testid="contact-form" noValidate>
              <input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                value={form.website}
                onChange={(e) => set("website", e.target.value)}
                style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-mono-spec text-slate-400 mb-1">{t.contact.company}</label>
                  <input
                    data-testid="contact-company"
                    type="text"
                    placeholder={t.contact.companyPh}
                    className={`${darkInput} ${errors.company ? "border-red-500" : ""}`}
                    value={form.company}
                    onChange={(e) => set("company", e.target.value)}
                  />
                  {errors.company && <p className="text-xs text-red-400 mt-1" data-testid="contact-company-error">{errors.company}</p>}
                </div>
                <div>
                  <label className="block text-xs font-mono-spec text-slate-400 mb-1">{t.contact.person}</label>
                  <input
                    data-testid="contact-person"
                    type="text"
                    placeholder={t.contact.personPh}
                    className={`${darkInput} ${errors.contact_person ? "border-red-500" : ""}`}
                    value={form.contact_person}
                    onChange={(e) => set("contact_person", e.target.value)}
                  />
                  {errors.contact_person && <p className="text-xs text-red-400 mt-1" data-testid="contact-person-error">{errors.contact_person}</p>}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-mono-spec text-slate-400 mb-1">{t.contact.email}</label>
                  <input
                    data-testid="contact-email"
                    type="email"
                    placeholder={t.contact.emailPh}
                    className={`${darkInput} ${errors.email ? "border-red-500" : ""}`}
                    value={form.email}
                    onChange={(e) => set("email", e.target.value)}
                  />
                  {errors.email && <p className="text-xs text-red-400 mt-1" data-testid="contact-email-error">{errors.email}</p>}
                </div>
                <div>
                  <label className="block text-xs font-mono-spec text-slate-400 mb-1">{t.contact.quota}</label>
                  <input
                    data-testid="contact-quota"
                    type="text"
                    placeholder={t.contact.quotaPh}
                    className={darkInput}
                    value={form.quota}
                    onChange={(e) => set("quota", e.target.value)}
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-mono-spec text-slate-400 mb-1">{t.contact.message}</label>
                <textarea
                  data-testid="contact-message"
                  rows={3}
                  placeholder={t.contact.messagePh}
                  className={darkInput}
                  value={form.message}
                  onChange={(e) => set("message", e.target.value)}
                />
              </div>
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-2">
                <span className="text-xs text-slate-500 font-mono-spec">{t.contact.note}</span>
                <button
                  data-testid="contact-submit-btn"
                  type="submit"
                  disabled={sending || !valid}
                  className="w-full sm:w-auto px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white font-mono-spec text-xs font-bold rounded-full uppercase tracking-wider transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {sending ? t.contact.sending : t.contact.submit}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
